import { ReplyPresence } from './ReplyPresence.js';
import { emailIds } from '../shared/threading.js';
import { t } from './i18n.js';
import { useEffect, useRef, useState } from 'react';
import {
  X,
  Minus,
  Maximize2,
  Send,
  Paperclip,
  Trash2,
  Bold,
  Italic,
  Underline,
  List,
  Check,
  Cloud,
  LoaderCircle,
  Copy,
  FileText,
  Clock,
} from 'lucide-react';
import type { Bootstrap, Message, Attachment, Draft, Submission } from '../shared/types.js';
import { api, send, uploadFile } from './api.js';
import { IconButton, Notice, formatBytes, displayDate } from './components.js';
import { RecipientField } from './RecipientField.js';
import {
  browserZone,
  exactLocalToIso,
  formatInZone,
  isoToLocal,
  localFromNow,
  localToIso,
  zones,
} from './time.js';
export type ComposeSeed = {
  mode: 'new' | 'reply' | 'replyAll' | 'forward' | 'draft';
  message?: Message;
  to?: string[];
};
function escape(s: string) {
  return s
    .replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
    )
    .replace(/\n/g, '<br/>');
}
const split = (s: string) =>
  s
    .split(/[,;\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
export function Composer({
  bootstrap,
  seed,
  onClose,
  onSaved,
  onSent,
}: {
  bootstrap: Bootstrap;
  seed: ComposeSeed;
  onClose: () => void;
  onSaved: () => void;
  onSent: (id: string, scheduledAt?: string | null) => void;
}) {
  const m = seed.message,
    isDraft = seed.mode === 'draft',
    isReply = ['reply', 'replyAll'].includes(seed.mode),
    subject = isDraft
      ? m!.subject
      : isReply
        ? `${/^re:/i.test(m!.subject) ? '' : 'Re: '}${m!.subject}`
        : seed.mode === 'forward'
          ? `Tr: ${m!.subject}`
          : '';
  const initialTo = isDraft
    ? m!.to
    : isReply
      ? [
          m!.from,
          ...(seed.mode === 'replyAll'
            ? m!.to.filter((a) => !bootstrap.fromAddresses.includes(a) && a !== m!.from)
            : []),
        ]
      : (seed.to ?? []);
  const personalSignatureText = bootstrap.user.signatureText ?? bootstrap.user.signature;
  const personalSignatureHtml = bootstrap.user.signatureHtml ?? escape(personalSignatureText);
  const signatureText = [personalSignatureText, bootstrap.companySignature?.text]
    .filter(Boolean)
    .join('\n\n');
  const signatureHtml = [personalSignatureHtml, bootstrap.companySignature?.html]
    .filter(Boolean)
    .join('<br><br>');
  const forwardedText =
    seed.mode === 'forward'
      ? `---------- ${t('Message transféré')} ----------\n${t('De')} : ${m!.from}\n${t('Objet')} : ${m!.subject}\n\n${m!.text}`
      : '';
  const initialText = isDraft
    ? m!.text
    : `${signatureText ? `\n\n${signatureText}` : ''}${forwardedText ? `\n\n${forwardedText}` : ''}`;
  const initialHtml = isDraft
    ? m!.html || escape(m!.text)
    : `${signatureHtml ? `<div><br></div><div><br></div><div>${signatureHtml}</div>` : ''}${forwardedText ? `<br><br>${escape(forwardedText)}` : ''}`;
  const [to, setTo] = useState(initialTo.join(', ')),
    [cc, setCc] = useState(
      isDraft
        ? m!.cc.join(', ')
        : seed.mode === 'replyAll'
          ? m!.cc.filter((a) => !bootstrap.fromAddresses.includes(a)).join(', ')
          : '',
    ),
    [bcc, setBcc] = useState(isDraft ? m!.bcc.join(', ') : ''),
    [showCc, setShowCc] = useState(Boolean(cc || bcc)),
    [from, setFrom] = useState(
      isDraft ? m!.from : (bootstrap.mailbox?.address ?? bootstrap.user.email),
    ),
    [sendMode, setSendMode] = useState<'sendAs' | 'sendOnBehalf'>(
      bootstrap.mailbox?.rights?.includes('sendAs') ? 'sendAs' : 'sendOnBehalf',
    ),
    [sub, setSub] = useState(subject),
    [text, setText] = useState(initialText),
    [html, setHtml] = useState(initialHtml),
    [attachments, setAttachments] = useState<Attachment[]>(isDraft ? m!.attachments : []),
    [busy, setBusy] = useState(false),
    [uploading, setUploading] = useState<{ name: string; progress: number } | null>(null),
    [saved, setSaved] = useState(''),
    [error, setError] = useState(''),
    [conflict, setConflict] = useState(false),
    [expanded, setExpanded] = useState(false),
    [minimized, setMinimized] = useState(false),
    [dirty, setDirty] = useState(false),
    [scheduling, setScheduling] = useState(false),
    [when, setWhen] = useState(localFromNow(60)),
    // The departure zone is explicit: a device travelling between two zones must not silently
    // move an already chosen hour. It defaults to the device zone and is sent as an instant.
    [zone, setZone] = useState(browserZone()),
    [zoneInput, setZoneInput] = useState(browserZone()),
    [scheduled, setScheduled] = useState<Submission>();
  const [teamTemplates, setTeamTemplates] = useState<
    { id: string; name: string; subject: string; html: string; text: string }[]
  >([]);
  const [chosenTemplate, setChosenTemplate] = useState('');
  useEffect(() => {
    if (bootstrap.proCollaboration && seed.mode === 'new')
      void api<typeof teamTemplates>('/team-mail/templates')
        .then(setTeamTemplates)
        .catch(() => {});
  }, [bootstrap.proCollaboration, seed.mode]);
  // A draft that already waits for a scheduled departure is shown read-only until cancelled.
  useEffect(() => {
    if (!isDraft || !m) return;
    api<Submission[]>('/submissions/scheduled')
      .then((list) => setScheduled(list.find((s) => s.draftId === m.id)))
      .catch(() => {});
  }, [isDraft, m]);
  const editor = useRef<HTMLDivElement>(null),
    fileInput = useRef<HTMLInputElement>(null),
    version = useRef({
      id: isDraft ? m!.id : undefined,
      version: isDraft ? m!.version : undefined,
    }),
    running = useRef<Promise<any> | null>(null),
    lastSaved = useRef(''),
    alive = useRef(true),
    sending = useRef(false),
    sendKey = useRef(crypto.randomUUID()),
    latest = useRef<Draft>(null!);
  latest.current = {
    to: split(to),
    cc: split(cc),
    bcc: split(bcc),
    from,
    ...(bootstrap.mailbox?.kind === 'shared' ? { sendMode } : {}),
    ...(isReply && m?.emailMessageId
      ? {
          replyTo: m.emailMessageId,
          references: emailIds([...(m.references ?? []), m.emailMessageId]),
        }
      : isDraft
        ? { replyTo: m?.inReplyTo, references: m?.references }
        : {}),
    subject: sub,
    text,
    html,
    attachmentIds: attachments.map((a) => a.id),
  };
  useEffect(() => {
    alive.current = true;
    if (editor.current) editor.current.innerHTML = initialHtml;
    return () => {
      alive.current = false;
    };
  }, []);
  const forwardStarted = useRef(false);
  useEffect(() => {
    if (seed.mode !== 'forward' || !m?.attachments.length || forwardStarted.current) return;
    forwardStarted.current = true;
    void (async () => {
      for (const a of m.attachments) {
        setUploading({ name: a.name, progress: 0 });
        try {
          const copied = await send(`/messages/${m.id}/attachments/${a.id}/copy`, {});
          if (alive.current) {
            setAttachments((prev) => [...prev, copied]);
            setDirty(true);
          }
        } catch (e: any) {
          if (alive.current)
            setError(t('Pièce jointe {name} : {error}', { name: a.name, error: e.message }));
        } finally {
          if (alive.current) setUploading(null);
        }
      }
    })();
  }, []);
  const saveOperation = useRef(crypto.randomUUID());
  const save = async (): Promise<any> => {
    if (running.current) {
      await running.current;
      if (JSON.stringify(latest.current) === lastSaved.current) return version.current;
    }
    const snapshot = { ...latest.current },
      serialized = JSON.stringify(snapshot);
    if (serialized === lastSaved.current) return version.current;
    if ([...snapshot.to, ...snapshot.cc, ...snapshot.bcc].some((a) => !/^\S+@\S+\.\S+$/.test(a)))
      throw new Error(t('Terminez la saisie du destinataire pour enregistrer le brouillon.'));
    setSaved(t('Enregistrement…'));
    const task = send(
      version.current.id ? `/drafts/${version.current.id}` : '/drafts',
      { ...snapshot, ...version.current, operationId: saveOperation.current },
      version.current.id ? 'PUT' : 'POST',
    )
      .then((r) => {
        version.current = { id: r.id, version: r.version };
        saveOperation.current = crypto.randomUUID();
        lastSaved.current = serialized;
        if (alive.current) {
          setSaved(t('Enregistré sur Vename Mail'));
          setDirty(JSON.stringify(latest.current) !== serialized);
          setError('');
          onSaved();
        }
        return r;
      })
      .catch((e) => {
        if (alive.current) {
          setSaved(t('Brouillon conservé dans cet onglet'));
          setError(e.message);
          if (e.status === 409) setConflict(true);
        }
        throw e;
      })
      .finally(() => {
        running.current = null;
      });
    running.current = task;
    return task;
  };
  useEffect(() => {
    if (!dirty || sending.current || conflict) return;
    const timer = setTimeout(() => void save().catch(() => {}), 900);
    return () => clearTimeout(timer);
  }, [to, cc, bcc, sub, text, html, attachments, from, sendMode, dirty, conflict]);
  useEffect(() => {
    const unload = (e: BeforeUnloadEvent) => {
      if (dirty || uploading || busy) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', unload);
    const online = () => {
      if (dirty) void save().catch(() => {});
    };
    window.addEventListener('online', online);
    return () => {
      window.removeEventListener('beforeunload', unload);
      window.removeEventListener('online', online);
    };
  }, [dirty, uploading, busy]);
  const [uncertain, setUncertain] = useState(false);
  const pendingPayload = useRef<{ body: any; key: string } | undefined>(undefined);
  const reconcile = async () => {
    setBusy(true);
    try {
      const r = await api(`/submissions/by-key/${pendingPayload.current?.key}`);
      onSent(r.id);
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const change = (fn: () => void) => {
    if (busy || uncertain || scheduled) return;
    fn();
    setDirty(true);
  };
  const close = async () => {
    if (uploading || busy) return;
    if (uncertain) {
      setError(t('Vérifiez la prise en charge ou réessayez le même envoi avant de fermer.'));
      return;
    }
    try {
      if (dirty) await save();
      onClose();
    } catch (e: any) {
      setError(e.message);
    }
  };
  const performSend = async (scheduledAt?: string) => {
    if (sending.current || uploading || conflict || scheduled) return;
    setError('');
    sending.current = true;
    setBusy(true);
    try {
      if (!latest.current.to.length && !latest.current.cc.length && !latest.current.bcc.length)
        throw new Error(t('Ajoutez au moins un destinataire.'));
      if (!pendingPayload.current) {
        await save();
        pendingPayload.current = {
          body: {
            ...latest.current,
            ...version.current,
            ...(scheduledAt ? { scheduledAt } : {}),
          },
          key: sendKey.current,
        };
      }
      const r = await send<Submission>('/submissions', pendingPayload.current.body, 'POST', {
        'Idempotency-Key': pendingPayload.current.key,
      });
      onSent(r.id, r.scheduledAt);
      onClose();
    } catch (e: any) {
      setError(e.message);
      // Uncertain only when the send request itself may have reached the server; a failed save
      // before it sent nothing, and the message stays editable.
      if ((e.status === 0 || e.status >= 500) && pendingPayload.current) setUncertain(true);
      else {
        pendingPayload.current = undefined;
        setUncertain(false);
        sendKey.current = crypto.randomUUID();
      }
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };
  const schedule = () => {
    if (skipped) {
      setError(t('Cette heure n’existe pas dans ce fuseau, à cause du changement d’heure.'));
      return;
    }
    const iso = exactLocalToIso(when, zone);
    const at = iso ? new Date(iso) : new Date(NaN);
    if (Number.isNaN(at.getTime()) || at.getTime() < Date.now() + 60_000) {
      setError(t('Choisissez une heure d’envoi dans au moins une minute.'));
      return;
    }
    setScheduling(false);
    void performSend(at.toISOString());
  };
  const presetAt = (dayOffset: number, hour: number) => {
    // Presets are expressed in the chosen zone, not in the device zone.
    const today = isoToLocal(new Date().toISOString(), zone).slice(0, 10);
    const day = new Date(`${today}T00:00:00Z`);
    day.setUTCDate(day.getUTCDate() + dayOffset);
    return `${day.toISOString().slice(0, 10)}T${String(hour).padStart(2, '0')}:00`;
  };
  const departure = exactLocalToIso(when, zone);
  // A value that converts but does not come back unchanged fell into the spring-forward gap.
  const skipped = Boolean(when && localToIso(when, zone) && !departure);
  const unschedule = async () => {
    if (!scheduled) return;
    setBusy(true);
    try {
      await send(`/submissions/${scheduled.id}/cancel`, {});
      setScheduled(undefined);
      setSaved(t('Programmation annulée'));
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const addFiles = async (files: FileList | null) => {
    if (!files || uncertain || busy) return;
    for (const f of Array.from(files)) {
      if (attachments.reduce((s, a) => s + a.size, 0) + f.size > 18 * 1024 ** 2) {
        setError(t('Les pièces jointes ne peuvent pas dépasser 18 Mio au total.'));
        break;
      }
      setUploading({ name: f.name, progress: 0 });
      try {
        const a = await uploadFile(f, (p) => setUploading({ name: f.name, progress: p }));
        setAttachments((prev) => [...prev, a]);
        setDirty(true);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setUploading(null);
      }
    }
    if (fileInput.current) fileInput.current.value = '';
  };
  const discard = async () => {
    if (uncertain) {
      setError(t('Vérifiez le résultat de l’envoi avant de supprimer le brouillon.'));
      return;
    }
    if (!window.confirm(t('Supprimer définitivement ce brouillon ?'))) return;
    setBusy(true);
    try {
      if (running.current) await running.current;
      if (version.current.id)
        await send('/messages/actions', {
          ids: [version.current.id],
          action: 'delete',
          confirm: true,
        });
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const format = (command: string) => {
    if (busy || uncertain) return;
    editor.current?.focus();
    document.execCommand(command);
    if (editor.current) {
      setHtml(editor.current.innerHTML);
      setText(editor.current.innerText);
      setDirty(true);
    }
  };
  return (
    <section
      className={`composer ${expanded ? 'expanded' : ''} ${minimized ? 'minimized' : ''}`}
      role="dialog"
      aria-label={t('Rédiger un message')}
    >
      {(isReply || (isDraft && m?.inReplyTo)) && m && bootstrap.mailbox?.kind === 'shared' && (
        <ReplyPresence messageId={m.id} mailboxId={bootstrap.mailbox.id} />
      )}
      <header className="composer-head">
        <div>
          <span className="compose-dot" />
          {isReply
            ? t('Répondre au message')
            : isDraft
              ? t('Modifier le brouillon')
              : t('Nouveau message')}
        </div>
        <div>
          <IconButton
            label={minimized ? t('Développer') : t('Réduire')}
            onClick={() => setMinimized(!minimized)}
          >
            <Minus size={17} />
          </IconButton>
          <IconButton
            label={expanded ? t('Réduire la fenêtre') : t('Agrandir')}
            onClick={() => setExpanded(!expanded)}
          >
            <Maximize2 size={15} />
          </IconButton>
          <IconButton
            label={t('Fermer et conserver le brouillon')}
            onClick={() => void close()}
            disabled={busy || !!uploading}
          >
            <X size={19} />
          </IconButton>
        </div>
      </header>
      <div className="composer-content">
        {bootstrap.mailbox?.kind === 'shared' && (
          <div className="compose-field">
            <label htmlFor="send-mode">{t('Mode d’envoi')}</label>
            <select
              id="send-mode"
              value={sendMode}
              disabled={busy || uncertain}
              onChange={(e) =>
                change(() => setSendMode(e.target.value as 'sendAs' | 'sendOnBehalf'))
              }
            >
              {bootstrap.mailbox.rights?.includes('sendAs') && (
                <option value="sendAs">En tant que {bootstrap.mailbox.address}</option>
              )}
              {bootstrap.mailbox.rights?.includes('sendOnBehalf') && (
                <option value="sendOnBehalf">
                  {bootstrap.user.name} au nom de {bootstrap.mailbox.address}
                </option>
              )}
            </select>
          </div>
        )}
        <div className="compose-field">
          <label htmlFor="compose-from">{t('De')}</label>
          <select
            id="compose-from"
            disabled={busy || uncertain}
            value={from}
            onChange={(e) => change(() => setFrom(e.target.value))}
          >
            {bootstrap.fromAddresses.map((a) => (
              <option key={a}>{a}</option>
            ))}
          </select>
        </div>
        <RecipientField
          id="compose-to"
          label={t('À')}
          disabled={busy || uncertain}
          autoFocus
          value={to}
          onChange={(v) => change(() => setTo(v))}
        >
          <button className="text-button" onClick={() => setShowCc(!showCc)}>
            {t('Cc / Cci')}
          </button>
        </RecipientField>
        {showCc && (
          <>
            <RecipientField
              id="compose-cc"
              label={t('Cc')}
              disabled={busy || uncertain}
              value={cc}
              onChange={(v) => change(() => setCc(v))}
            />
            <RecipientField
              id="compose-bcc"
              label={t('Cci')}
              disabled={busy || uncertain}
              value={bcc}
              onChange={(v) => change(() => setBcc(v))}
            />
          </>
        )}
        <div className="compose-field subject">
          <label htmlFor="compose-subject">{t('Objet')}</label>
          <input
            id="compose-subject"
            disabled={busy || uncertain}
            value={sub}
            onChange={(e) => change(() => setSub(e.target.value))}
            placeholder={t('De quoi souhaitez-vous parler ?')}
            maxLength={998}
          />
        </div>
        {seed.mode === 'new' && teamTemplates.length > 0 && (
          <div className="compose-field">
            <label htmlFor="compose-team-template">{t('Modèle d’équipe')}</label>
            <select
              id="compose-team-template"
              value={chosenTemplate}
              disabled={busy || uncertain}
              onChange={(e) => setChosenTemplate(e.target.value)}
            >
              <option value="">{t('Choisir un modèle')}</option>
              {teamTemplates.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!chosenTemplate || busy || uncertain}
              onClick={() => {
                const item = teamTemplates.find((x) => x.id === chosenTemplate);
                if (!item || !editor.current) return;
                change(() => {
                  if (!sub.trim() && item.subject) setSub(item.subject);
                  editor.current!.innerHTML = `${item.html}<br><br>${editor.current!.innerHTML}`;
                  setHtml(editor.current!.innerHTML);
                  setText(editor.current!.innerText);
                });
                setChosenTemplate('');
              }}
            >
              {t('Insérer')}
            </button>
          </div>
        )}
        <div
          ref={editor}
          contentEditable={!busy && !uncertain}
          suppressContentEditableWarning
          role="textbox"
          aria-label={t('Corps du message')}
          aria-multiline="true"
          className="message-editor"
          data-placeholder={t('Votre message…')}
          onInput={(e) =>
            change(() => {
              setText(e.currentTarget.innerText);
              setHtml(e.currentTarget.innerHTML);
            })
          }
        />
        {attachments.length > 0 && (
          <div className="compose-attachments">
            {attachments.map((a) => (
              <div key={a.id} className="compose-attachment">
                <FileText size={17} />
                <div>
                  <strong>{a.name}</strong>
                  <small>
                    {formatBytes(a.size)} ·{' '}
                    {a.scanStatus === 'local_unscanned'
                      ? t('Fichier local, non analysé')
                      : a.scanStatus === 'clean'
                        ? t('Analyse terminée')
                        : a.scanStatus === 'blocked'
                          ? t('Fichier bloqué')
                          : t('Analyse en attente')}
                  </small>
                </div>
                <IconButton
                  label={t('Retirer {name}', { name: a.name })}
                  onClick={() =>
                    change(() => setAttachments(attachments.filter((x) => x.id !== a.id)))
                  }
                >
                  <X size={15} />
                </IconButton>
              </div>
            ))}
          </div>
        )}
        {uploading && (
          <div className="upload-progress" role="status">
            <LoaderCircle size={15} className="spin" />
            {uploading.name} · {uploading.progress} %
            <progress
              value={uploading.progress}
              max="100"
              aria-label={t('Progression du téléversement')}
            />
          </div>
        )}
        {error && (
          <Notice type="error">
            {error}
            {uncertain && (
              <button className="text-button" onClick={() => void reconcile()} disabled={busy}>
                {t('Vérifier la prise en charge')}
              </button>
            )}
            {conflict && (
              <button
                className="text-button"
                onClick={() => {
                  version.current = { id: undefined, version: undefined };
                  lastSaved.current = '';
                  setConflict(false);
                  setDirty(true);
                }}
              >
                {t('Enregistrer une copie distincte')}
              </button>
            )}
          </Notice>
        )}
        {scheduled && (
          <Notice type="info">
            {t(
              'Cet envoi est programmé le {date}. Annulez la programmation pour modifier ce message.',
              { date: displayDate(scheduled.scheduledAt!, true) },
            )}{' '}
            <button type="button" className="text-button" onClick={() => void unschedule()}>
              {t('Annuler la programmation')}
            </button>
          </Notice>
        )}
        {scheduling && (
          <div className="schedule-panel" role="group" aria-label={t('Programmer l’envoi')}>
            <label htmlFor="schedule-when">{t('Envoyer le')}</label>
            <input
              id="schedule-when"
              type="datetime-local"
              value={when}
              min={localFromNow(2, zone)}
              onChange={(e) => setWhen(e.target.value)}
            />
            <label htmlFor="schedule-zone">{t('Fuseau horaire')}</label>
            <input
              id="schedule-zone"
              list="schedule-zones"
              value={zoneInput}
              onChange={(e) => setZoneInput(e.target.value)}
              onBlur={() => {
                try {
                  new Intl.DateTimeFormat(undefined, { timeZone: zoneInput });
                  setZone(zoneInput);
                  setError('');
                } catch {
                  setZoneInput(zone);
                  setError(t('Fuseau inconnu. Choisissez un fuseau de la liste.'));
                }
              }}
            />
            <datalist id="schedule-zones">
              {[...new Set([...zones, browserZone()])].map((z) => (
                <option key={z} value={z} />
              ))}
            </datalist>
            <small role={skipped ? 'alert' : undefined}>
              {departure
                ? t('Départ le {date} ({zone}).', {
                    date: formatInZone(departure, zone),
                    zone,
                  })
                : skipped
                  ? t('Cette heure n’existe pas dans ce fuseau, à cause du changement d’heure.')
                  : t('Choisissez une date et une heure.')}
            </small>
            {zone !== browserZone() && departure && (
              <small className="schedule-elsewhere">
                {t('Soit {date} sur cet appareil ({zone}).', {
                  date: formatInZone(departure, browserZone()),
                  zone: browserZone(),
                })}
              </small>
            )}
            <div className="schedule-presets">
              <button type="button" className="text-button" onClick={() => setWhen(presetAt(1, 8))}>
                {t('Demain 8 h')}
              </button>
              <button
                type="button"
                className="text-button"
                onClick={() => {
                  // The weekday is the one of the chosen zone, which may differ from the device.
                  const today = new Date(isoToLocal(new Date().toISOString(), zone) + ':00Z');
                  setWhen(presetAt((8 - today.getUTCDay()) % 7 || 7, 8));
                }}
              >
                {t('Lundi 8 h')}
              </button>
            </div>
            <div className="modal-actions">
              <button type="button" className="text-button" onClick={() => setScheduling(false)}>
                {t('Fermer')}
              </button>
              <button type="button" className="primary" onClick={schedule} disabled={busy}>
                {t('Programmer')}
              </button>
            </div>
          </div>
        )}
        <div className="format-toolbar">
          <IconButton label={t('Gras')} onClick={() => format('bold')}>
            <Bold size={17} />
          </IconButton>
          <IconButton label={t('Italique')} onClick={() => format('italic')}>
            <Italic size={17} />
          </IconButton>
          <IconButton label={t('Souligné')} onClick={() => format('underline')}>
            <Underline size={17} />
          </IconButton>
          <span className="toolbar-separator" />
          <IconButton label={t('Liste à puces')} onClick={() => format('insertUnorderedList')}>
            <List size={18} />
          </IconButton>
          <span className="save-status" role="status">
            {saved === t('Enregistré sur Vename Mail') ? <Check size={14} /> : <Cloud size={14} />}{' '}
            {saved || t('Brouillon')}
          </span>
        </div>
        <footer className="composer-footer">
          <button
            className="primary"
            onClick={() => void performSend()}
            disabled={
              busy ||
              !!uploading ||
              conflict ||
              !!scheduled ||
              attachments.some((a) => ['pending', 'blocked'].includes(a.scanStatus))
            }
          >
            <Send size={17} />
            {busy ? t('Envoi en cours…') : uncertain ? t('Réessayer le même envoi') : t('Envoyer')}
          </button>
          <IconButton
            label={t('Programmer l’envoi')}
            active={scheduling}
            onClick={() => setScheduling(!scheduling)}
            disabled={busy || !!uploading || conflict || !!scheduled || uncertain}
          >
            <Clock size={19} />
          </IconButton>
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            onChange={(e) => void addFiles(e.target.files)}
          />
          <IconButton
            label={t('Joindre des fichiers')}
            onClick={() => fileInput.current?.click()}
            disabled={busy || !!uploading}
          >
            <Paperclip size={20} />
          </IconButton>
          <span className="spacer" />
          <IconButton
            label={t('Copier le texte du message')}
            onClick={() =>
              void navigator.clipboard
                .writeText(text)
                .then(() => setSaved(t('Texte copié')))
                .catch(() => setError(t('Sélectionnez le texte pour le copier.')))
            }
          >
            <Copy size={18} />
          </IconButton>
          <IconButton
            label={t('Supprimer le brouillon')}
            onClick={() => void discard()}
            disabled={busy}
          >
            <Trash2 size={19} />
          </IconButton>
        </footer>
      </div>
    </section>
  );
}
